import React, { useMemo } from "react";
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import "./RiskOpportunityBuckets.css";
import "../../../../styles/dashboardCardTokens.css";
import "../../../../styles/statusTokens.css";

// Diamond Icon Component for Elite
const DiamondIcon = () => (
  <div className="diamond-icon-elite">
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 2L12 6L8 14L4 6L8 2Z" fill="currentColor" />
    </svg>
  </div>
);

const BUCKET_COPY = {
  elite: {
    performanceText: "Turning +20% faster than expected",
    bulletText: "Turning:28% faster than expected",
  },
  strong: {
    performanceText: "On pace",
    bulletText: "Risk factors",
  },
  watch: {
    performanceText: "Turning slower than expected • 21%",
    bulletText: "Adjust pricing",
  },
  risk: {
    performanceText: "Turning -37% behind pace",
    bulletText: "Recommend to action",
  },
};

export default function RiskOpportunityBuckets({ vehicles = [], bucketCounts, activeBucket, onBucketClick }) {
  const buckets = useMemo(() => {
    if (bucketCounts && typeof bucketCounts === "object") {
      const toBucket = (key) => {
        const b = bucketCounts[key] || {};
        const units = b.units || [];
        const copy = BUCKET_COPY[key?.toLowerCase()] || {};
        return {
          count: b.count ?? 0,
          totalValue: b.value ?? 0,
          performanceText: copy.performanceText || "",
          bulletText: copy.bulletText || "",
          vehicles: units,
        };
      };
      return {
        elite: toBucket("ELITE"),
        strong: toBucket("STRONG"),
        watch: toBucket("WATCH"),
        risk: toBucket("RISK"),
      };
    }

    // Fallback: compute from vehicles
    const elite = vehicles.filter(v => {
      const dom = v.dom || 0;
      const delta = v.market_avg_delta || 0;
      return dom <= 7 && delta < -5;
    });
    const strong = vehicles.filter(v => {
      const dom = v.dom || 0;
      const delta = v.market_avg_delta || 0;
      return dom <= 7 && Math.abs(delta) < 5;
    });
    const watch = vehicles.filter(v => {
      const dom = v.dom || 0;
      const delta = v.market_avg_delta || 0;
      return dom > 7 && dom <= 30 && Math.abs(delta) < 10;
    });
    const risk = vehicles.filter(v => {
      const dom = v.dom || 0;
      const delta = v.market_avg_delta || 0;
      return dom > 30 || delta > 10;
    });

    const calculateMetrics = (bucket, key) => {
      const totalValue = bucket.reduce((sum, v) => sum + (v.price || 0), 0);
      const copy = BUCKET_COPY[key] || {};
      return {
        count: bucket.length,
        totalValue,
        performanceText: copy.performanceText || "",
        bulletText: copy.bulletText || "",
        vehicles: bucket,
      };
    };

    return {
      elite: calculateMetrics(elite, "elite"),
      strong: calculateMetrics(strong, "strong"),
      watch: calculateMetrics(watch, "watch"),
      risk: calculateMetrics(risk, "risk"),
    };
  }, [vehicles, bucketCounts]);

  return (
    <div className="inventory-performance-overview">
      <div className="inventory-performance-grid">
        {/* Elite Card */}
        <div
          className={`inventory-performance-card inventory-card-elite${activeBucket === "elite" ? " inventory-card-active" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => onBucketClick?.("elite")}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onBucketClick?.("elite")}
        >
          <div className="inventory-card-header">
            <div className="inventory-card-icon-wrap">
              <DiamondIcon />
            </div>
            <div className="inventory-card-title-wrap">
              <span className="inventory-card-title">Elite</span>
            </div>
          </div>
          <div className="inventory-card-metrics">
            <span className="inventory-card-units">{buckets.elite.count} Units</span>
            <span className="inventory-card-separator"></span>
            <span className="inventory-card-value">${buckets.elite.totalValue.toLocaleString()}</span>
          </div>
        </div>

        {/* Strong Card */}
        <div
          className={`inventory-performance-card inventory-card-strong${activeBucket === "strong" ? " inventory-card-active" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => onBucketClick?.("strong")}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onBucketClick?.("strong")}
        >
          <div className="inventory-card-header">
            <div className="inventory-card-icon-wrap inventory-icon-circle inventory-icon-circle-strong">
              <CheckCircleOutlined className="inventory-card-icon inventory-icon-strong" />
            </div>
            <div className="inventory-card-title-wrap">
              <span className="inventory-card-title">Strong</span>
            </div>
          </div>
          <div className="inventory-card-metrics">
            <span className="inventory-card-units">
              {buckets.strong.count} Units
              {buckets.strong.count > 0 && <ArrowUpOutlined className="inventory-card-arrow inventory-arrow-up" />}
            </span>
            <span className="inventory-card-separator inventory-separator-strong"></span>
            <span className="inventory-card-value">
              ${buckets.strong.totalValue.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Watch Card */}
        <div
          className={`inventory-performance-card inventory-card-watch${activeBucket === "watch" ? " inventory-card-active" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => onBucketClick?.("watch")}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onBucketClick?.("watch")}
        >
          <div className="inventory-card-header">
            <div className="inventory-card-icon-wrap inventory-icon-circle inventory-icon-circle-watch">
              <WarningOutlined className="inventory-card-icon inventory-icon-watch" />
            </div>
            <div className="inventory-card-title-wrap">
              <span className="inventory-card-title">Watch</span>
            </div>
          </div>
          <div className="inventory-card-metrics">
            <span className="inventory-card-units">
              {buckets.watch.count} Units
              {buckets.watch.count > 0 && <ArrowDownOutlined className="inventory-card-arrow inventory-arrow-down inventory-arrow-watch" />}
            </span>
            <span className="inventory-card-separator inventory-separator-watch"></span>
            <span className="inventory-card-value">
              ${buckets.watch.totalValue.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Risk Card */}
        <div
          className={`inventory-performance-card inventory-card-risk${activeBucket === "risk" ? " inventory-card-active" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => onBucketClick?.("risk")}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onBucketClick?.("risk")}
        >
          <div className="inventory-card-header">
            <div className="inventory-card-icon-wrap inventory-icon-circle inventory-icon-circle-risk">
              <CloseCircleOutlined className="inventory-card-icon inventory-icon-risk" />
            </div>
            <div className="inventory-card-title-wrap">
              <span className="inventory-card-title">Risk</span>
            </div>
          </div>
          <div className="inventory-card-metrics">
            <span className="inventory-card-units">
              {buckets.risk.count} Units
              {buckets.risk.count > 0 && <ArrowDownOutlined className="inventory-card-arrow inventory-arrow-down inventory-arrow-risk" />}
            </span>
            <span className="inventory-card-separator inventory-separator-risk"></span>
            <span className="inventory-card-value">
              ${buckets.risk.totalValue.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
